import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import {
  SearchForFacetValuesResponse,
  SearchResponse,
  MultipleQueriesResponse,
} from '@algolia/client-search';
import { getPublicHostName } from 'lib/util/urls';
import { logError, logWarning } from 'lib/util/errors';
import { env } from 'lib/env';
import { FilterCategoryDataLayerObject, gtmDatalayerPush } from './gtm-datalayer';

// Re-export search client and helper so that we use a consolidated version across the stack
export type { SearchClient };
export type { AlgoliaSearchHelper } from 'algoliasearch-helper';

// OK to use any here, as it's what matches Algolia's definitions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare type SearchRequestRecord = Record<string, any>;

/**
 * Default search index for this website and environment.
 * */
export const DEFAULT_SEARCH_INDEX = env.ALGOLIA_INDEX_NAME;

/**
 * Default search index for this website and environment.
 * */
export const REPORTS_SEARCH_INDEX = () => env.ALGOLIA_REPORTS_INDEX_NAME;

/**
 * Default suggestion index for this website and environment.
 * */
export const DEFAULT_SUGGEST_INDEX = env.ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME?.trim()
  ? env.ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME
  : `${env.ALGOLIA_INDEX_NAME}_suggestions`;

/**
 * Empty Algolia response
 * */
export const EMPTY_SEARCH_RESULT = {
  results: [
    {
      hits: [],
      nbHits: 0,
      page: 0,
      nbPages: 0,
      hitsPerPage: 10,
      exhaustiveNbHits: true,
      exhaustiveTypo: true,
      exhaustive: {
        nbHits: true,
        typo: true,
      },
      query: '',
      index: DEFAULT_SEARCH_INDEX,
    },
  ],
};

/**
 * Total Disease Sort. Used to identify virtual sort indices in the Algolia search client.
 * */

const VIRTUAL_SORT_PATTERN = /^(.+)_dt_(.+)_(asc|desc)$/;
const MAX_VIRTUAL_SORT_FETCH = 200;

/**
 * Builds a virtual sort index name for a given attribute. Requests sent to this
 * "index" are intercepted and resolved against `baseIndex` in `searchWithTransporter`.
 * @param baseIndex The real Algolia index to search against.
 * @param attribute The numeric attribute to sort by (e.g. `dt_6_0`).
 * @param isAscending Sort direction.
 * @returns A synthetic index name suitable for passing to `useSortBy`'s `refine`.
 * */
export const buildVirtualSortIndexName = (
  baseIndex: string,
  attribute: string,
  isAscending: boolean
): string => `${baseIndex}_${attribute}_${isAscending ? 'asc' : 'desc'}`;

interface VirtualSortMatch {
  realIndexName: string;
  attribute: string;
  isAscending: boolean;
}

const matchVirtualSortIndex = (indexName?: string): VirtualSortMatch | undefined => {
  const match = VIRTUAL_SORT_PATTERN.exec(indexName ?? '');
  if (!match) {
    return undefined;
  }
  return { realIndexName: match[1], attribute: `dt_${match[2]}`, isAscending: match[3] === 'asc' };
};

// True when the given index name is a synthetic virtual sort index
export const isVirtualSortIndex = (indexName?: string): boolean =>
  matchVirtualSortIndex(indexName) !== undefined;

interface VirtualSortRewrite extends VirtualSortMatch {
  requestIndex: number;
  originalIndexName: string;
  originalPage: number;
  originalHitsPerPage: number;
}

/**
 * Rewrites any virtual sort requests in-place to target their real index at
 * `MAX_VIRTUAL_SORT_FETCH` hits so the full matching set can be sorted below.
 * @param requests The batch of requests about to be sent to Algolia.
 * @returns Bookkeeping needed to re-sort and re-paginate the matching responses.
 * */
const rewriteVirtualSortRequests = (requests: SearchRequestRecord): VirtualSortRewrite[] => {
  const rewrites: VirtualSortRewrite[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requests.forEach((request: any, requestIndex: number) => {
    const match = matchVirtualSortIndex(request?.indexName);
    if (!match) {
      return;
    }
    rewrites.push({
      ...match,
      requestIndex,
      originalIndexName: request.indexName,
      originalPage: request.params?.page ?? 0,
      originalHitsPerPage: request.params?.hitsPerPage ?? 10,
    });
    // eslint-disable-next-line no-param-reassign
    request.indexName = match.realIndexName;
    // eslint-disable-next-line no-param-reassign
    request.params = { ...request.params, page: 0, hitsPerPage: MAX_VIRTUAL_SORT_FETCH };
  });
  return rewrites;
};

/**
 * Sorts and re-paginates the results of any virtual-sort requests back to the
 * page/hitsPerPage the caller originally asked for, restoring the synthetic
 * index name so downstream InstantSearch state stays consistent.
 * @param response The raw transporter response.
 * @param rewrites Bookkeeping produced by `rewriteVirtualSortRequests`.
 * */
// Algolia interface defines return type as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyVirtualSortRewrites = (response: any, rewrites: VirtualSortRewrite[]): void => {
  rewrites.forEach((rewrite) => {
    const result = response?.results?.[rewrite.requestIndex];
    if (!result?.hits) {
      return;
    }
    if (result.nbHits > MAX_VIRTUAL_SORT_FETCH) {
      logWarning(
        `Virtual sort index ${rewrite.originalIndexName} matched ${result.nbHits} hits, ` +
          `exceeding the ${MAX_VIRTUAL_SORT_FETCH} fetch cap used for client-side sorting; results are incomplete.`
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sortedHits = [...result.hits].sort((a: any, b: any) => {
      const aValue = a[rewrite.attribute] ?? 0;
      const bValue = b[rewrite.attribute] ?? 0;
      return rewrite.isAscending ? aValue - bValue : bValue - aValue;
    });
    const start = rewrite.originalPage * rewrite.originalHitsPerPage;
    result.hits = sortedHits.slice(start, start + rewrite.originalHitsPerPage);
    result.page = rewrite.originalPage;
    result.hitsPerPage = rewrite.originalHitsPerPage;
    result.nbHits = sortedHits.length;
    result.nbPages = Math.ceil(sortedHits.length / rewrite.originalHitsPerPage);
    result.index = rewrite.originalIndexName;
    result.indexName = rewrite.originalIndexName;
  });
};

export const isEmptySearchRequest = (requests: SearchRequestRecord): boolean => {
  // If more than 1 request return false
  if (requests.length > 1) {
    return false;
  }
  const theRequest = requests[0];
  // Virtual sort requests should never be treated as empty — they need the
  // full rewrite pipeline to run.
  if (isVirtualSortIndex(theRequest?.indexName)) {
    return false;
  }
  const filterExpression = /^startDate\s>=\s\d+\sOR\sstartDate\s=\s0+\sOR\sendDate\s>=\s\d+$/gm;
  if (
    theRequest?.params && // and has params
    (!theRequest.query || theRequest.query === ``) && // and has a missing or empty query
    (!theRequest.params.query || theRequest.params.query === ``) && // and has a missing or empty params query
    (!theRequest.params.facets || theRequest.params.facets.length === 0) && // and has missing or empty facets
    (!theRequest.params.filters || // and has no filters
      theRequest.params.filters === `` || // or empty filters
      filterExpression.test(theRequest.params.filters)) // or a default startDate filter
  ) {
    return true;
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pushFiltersDataLayerObject = (baseClient: any, value: any, requests?: any) => {
  const request = requests[0];

  if (request?.params?.facetFilters) {
    const facets = request?.params?.facetFilters;

    const gtmData: FilterCategoryDataLayerObject = {
      event: 'e_all_filters',
      contentType: request?.params?.filters,
      resultCount: value?.results[0]?.nbHits,
    };

    if (Array.isArray(facets)) {
      facets.forEach((facet: string[]) => {
        let facetName = '';
        let facetSelected = '';
        if (Array.isArray(facet)) {
          facet.forEach((data, j: number) => {
            const record = data?.split(':');
            if (record.length > 1) {
              if (j === 0) {
                facetName = record[0];
                facetSelected = record[1];
              } else {
                facetSelected = facetSelected?.concat(', ', record[1]);
              }
              gtmData[facetName] = facetSelected;
            }
          });
        }
      });
    }

    const serializedRequest = JSON.stringify(gtmData);
    if (baseClient._myLastFacetFilter !== serializedRequest) {
      // eslint-disable-next-line no-param-reassign
      baseClient._myLastFacetFilter = serializedRequest;
      gtmDatalayerPush(gtmData);
    }
  }
};

// Algolia interface defines return type as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const decodeResponseValues = (baseClient: SearchClient, value: any, requests: any) => {
  pushFiltersDataLayerObject(baseClient, value, requests);
  const fromServer = value;
  if (fromServer?.results) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fromServer.results.forEach((result: any, idx: number) => {
      if (result?.query && result?.query !== '') {
        fromServer.results[idx].query = decodeURIComponent(result.query);
        fromServer.results[idx].indexName = decodeURIComponent(result.indexName);
        fromServer.results[idx].highlightPreTag = decodeURIComponent(result.highlightPreTag);
        fromServer.results[idx].highlightPostTag = decodeURIComponent(result.highlightPostTag);
      }
    });
  }
  return fromServer;
};

// Algolia interface defines return type as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const decodeFailedResponse = (err: any) => {
  const isAuthFail =
    (err.name === 'DeserializationError' &&
      err.response &&
      /\/auth\/okta/i.test(err.response.content)) ||
    (err.name === 'RetryError' &&
      /Unreachable hosts - your application id may be incorrect/i.test(err.message));
  if (isAuthFail) {
    logError(`User not authenticated - ${err.name}`, err);
    return EMPTY_SEARCH_RESULT;
  }
  logError('Search transport error', err);
  throw err;
};

/**
 * Performs pre and post search processing.
 * @param baseClient The base search client.
 * @param page The search action.
 * @param requests Search Requests to process.
 */
export const searchWithTransporter = async (
  baseClient: SearchClient,
  page: string,
  requests: SearchRequestRecord,
  isNeverSearch = false
  // Algolia interface defines return type as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  // If an empty search request is sent
  if (isEmptySearchRequest(requests) || isNeverSearch === true) {
    // Return empty resultset without calling back-end
    const ret = { ...EMPTY_SEARCH_RESULT };
    // If the incoming request has an indexName, then use it to map the empty resultsets to the same index
    if (requests[0]?.indexName) {
      ret.results[0].index = requests[0]?.indexName;
    }
    return new Promise((resolve) => {
      // Use a timeout to simulate async behavior and allow for consistent promise handling
      setTimeout(() => resolve(ret), 5);
    });
  }
  const ifReportIndex =
    (REPORTS_SEARCH_INDEX() ?? '') !== '' &&
    `${requests[0]?.indexName}`.startsWith(REPORTS_SEARCH_INDEX() ?? '');
  const searchRoute = ifReportIndex ? `search-reports` : `search`;

  // Ensure URL params (txId, sortBy) are reflected in every request.
  // This guarantees the very first Algolia call has correct filters/sort
  // regardless of widget registration order.
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlTxId = urlParams.get('txId');
    const urlSortBy = urlParams.get('sortBy');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    requests.forEach((request: any) => {
      // Inject disease facet filter from txId if not already present
      if (urlTxId && request?.params) {
        const facetFilters = request.params.facetFilters ?? [];
        const hasDiseaseFilter =
          Array.isArray(facetFilters) &&
          facetFilters.some((f: string | string[]) =>
            Array.isArray(f) ? f.some((v) => v.startsWith('diseases:')) : f.startsWith('diseases:')
          );
        if (!hasDiseaseFilter) {
          request.params.facetFilters = [...facetFilters, [`diseases:${urlTxId}`]];
        }
      }

      // Apply virtual sort index from sortBy if the request is still on the base index
      if (urlSortBy && request?.indexName === env.ALGOLIA_INDEX_NAME) {
        const sortIndexName = urlSortBy.startsWith(env.ALGOLIA_INDEX_NAME)
          ? urlSortBy
          : `${env.ALGOLIA_INDEX_NAME}_${urlSortBy}`;
        if (isVirtualSortIndex(sortIndexName)) {
          request.indexName = sortIndexName;
        }
      }
    });
  }
  // Requests targeting a virtual sort index fetch the full matching set;
  // the response is re-sorted and re-paginated before anything else sees it.
  const virtualSortRewrites = rewriteVirtualSortRequests(requests);
  const isVirtualSort = virtualSortRewrites.length > 0;
  try {
    const x = await baseClient.transporter.read(
      {
        path: `/api/${searchRoute}/${page}`,
        method: `POST`,
        data: { requests },
        // Disable caching for virtual sort requests — we mutate hitsPerPage to 200
        // but the cache key uses the real index name, causing stale hitsPerPage:10
        // responses from prior non-virtual searches to be returned.
        cacheable: !isVirtualSort,
      },
      {
        cacheable: !isVirtualSort,
        headers: {
          'Content-Type': `application/json`,
        },
      }
    );
    if (isVirtualSort) {
      applyVirtualSortRewrites(x, virtualSortRewrites);
    }
    return decodeResponseValues(baseClient, x, requests);
  } catch (err) {
    return decodeFailedResponse(err);
  }
};

const createClientSideInstance = (
  props:
    | {
        baseClient: SearchClient;
        isNeverSearch?: boolean;
      }
    | SearchClient
): SearchClient => {
  let baseClient: SearchClient;
  let isNeverSearch: boolean;

  if (!props) {
    throw new Error(`Invalid search client configuration: no properties provided`);
  }
  if ('baseClient' in props) {
    baseClient = props.baseClient;
    isNeverSearch = props.isNeverSearch === true;
    baseClient.addAlgoliaAgent('NmdpNeverSearch.1.0.0');
  } else {
    baseClient = props;
  }
  if (!baseClient?.transporter) {
    throw new Error('Invalid search client configuration: baseClient is missing or invalid');
  }

  // Algolia defines as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const search = (requests: SearchRequestRecord): Promise<any> =>
    searchWithTransporter(baseClient, `query`, requests, isNeverSearch === true);

  // Algolia defines as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const searchForFacetValues = (requests: SearchRequestRecord): Promise<any> =>
    searchWithTransporter(baseClient, `sffv`, requests, isNeverSearch === true);

  return {
    ...baseClient,
    search,
    searchForFacetValues,
  };
};

const publicHost = getPublicHostName();

/**
 * Creates a new Algolia search client.
 * Initializes and returns a connection to Algolia
 * @returns An initialized search client.
 * */
export const createSearchClient = (forServer = false): SearchClient => {
  return forServer === true
    ? createClientSideInstance({ baseClient: algoliasearch('', ''), isNeverSearch: true })
    : createClientSideInstance(
        algoliasearch('', '', {
          hosts: [publicHost], // Route requests thru vercel to restrict API key access
          timeouts: {
            connect: 2, // Connection timeout in seconds
            read: 10, // Read timeout in seconds
            write: 30, // Write timeout in seconds
          },
        })
      );
};

/**
 * Calculates a report download url given its object id.
 * @param objectId The algolia identifier of the report to download.
 * @returns The path to download the report.
 * */
export const getReportDownloadUrl = (objectId: string): string => {
  const normalizedObjectId = objectId.replaceAll('\\', '/');
  return `/api/search-reports/download/${normalizedObjectId}`;
};

/**
 * Typeguard used to verify we were returned a SearchResponse not a SearchForFacetValuesResponse.
 * @param check the object to evaluate
 * @returns true if the object is of type SearchResponse
 * */
export const isSearchResults = <TObject = {}>(
  check: SearchForFacetValuesResponse | SearchResponse<TObject> | undefined
): check is SearchResponse<TObject> => typeof check !== 'undefined' && 'hits' in check;
/**
 * Typeguard used to verify the result at a given index is a SearchResponse type.
 * @param response The @see MultipleQueriesResponse to evaluate
 * @param index The index of the response to evaluate.  If not set, the first item is used.
 * @returns A @see SearchResponse value, or undefined.
 * */
export const getSearchResultsAt = <TObject = {}>(
  response?: MultipleQueriesResponse<TObject>,
  index?: number
): SearchResponse<TObject> | undefined => {
  // Typeguard against undefined
  if (typeof response === 'undefined') {
    return undefined;
  }
  // Make sure we were passed a valid index
  const theIndex = typeof index === 'undefined' ? 0 : index;
  if (response.results.length <= theIndex) {
    return undefined;
  }
  // Typeguard the value and return it
  const theValueToCheck = response.results[theIndex];
  return isSearchResults(theValueToCheck) ? theValueToCheck : undefined;
};
